import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { FormasDeEntrar, leerFormasDeEntrar } from 'app/core/auth/social/social-auth.logic';
import { UserService } from 'app/core/user/user.service';
import { User } from 'app/core/user/user.types';
import { environment } from 'environments/environment';
import { BehaviorSubject, combineLatest, distinctUntilChanged, filter, map, Observable, shareReplay } from 'rxjs';

import { perfilCompleto, PerfilCompleto } from './perfil-completo';

/**
 * El porcentaje del perfil para el anillo del avatar (menú y perfil) y la
 * tarjeta de pendientes. Junta el usuario de `UserService` (se relee tras cada
 * PATCH /users/me, verificar WhatsApp o subir foto) con las formas de entrar
 * (`GET /auth/social/identities`), que el perfil vuelve a publicar aquí cada
 * vez que vincula o desvincula.
 */
@Injectable({ providedIn: 'root' })
export class PerfilCompletoService {
    private _http = inject(HttpClient);
    private _userService = inject(UserService);

    /** `undefined` = todavía no llegan; `null` = no se pudieron leer (404, red). */
    private _formas = new BehaviorSubject<FormasDeEntrar | null | undefined>(undefined);
    /** Descarta la respuesta de una petición vieja (otra cuenta, o ya refrescada). */
    private _turno = 0;

    /**
     * `null` mientras no hay perfil real (cargando, o el usuario de respaldo
     * cuando `/users/me` falla): sin datos no se pinta ningún anillo.
     */
    readonly progreso$: Observable<PerfilCompleto | null> = combineLatest([
        this._userService.user$,
        this._formas,
    ]).pipe(
        map(([user, formas]) => (tieneDatos(user) ? perfilCompleto(user, formas ?? null) : null)),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    constructor() {
        // Cada cuenta lee sus propias formas de entrar: al llegar el perfil
        // (o cambiar de cuenta en la misma pestaña) se piden de nuevo.
        this._userService.user$
            .pipe(
                filter(tieneDatos),
                map((u) => u.id),
                distinctUntilChanged(),
            )
            .subscribe(() => {
                this._formas.next(undefined);
                this.refrescarFormas();
            });
    }

    /** Vuelve a pedir las formas de entrar. Nunca muestra error. */
    refrescarFormas(): void {
        const turno = ++this._turno;
        this._http.get(`${environment.apiUrl}/auth/social/identities`).subscribe({
            next: (res) => {
                if (turno === this._turno) this._formas.next(leerFormasDeEntrar(res));
            },
            error: () => {
                if (turno === this._turno) this._formas.next(null);
            },
        });
    }

    /** El perfil ya las leyó (o no pudo): se reusan sin otra petición. */
    ponerFormas(formas: FormasDeEntrar | null): void {
        this._turno++;
        this._formas.next(formas);
    }
}

/** El mapeo de `/users/me` siempre trae `opc_email_verified` como booleano. */
function tieneDatos(user: User | null | undefined): user is User {
    return !!user && typeof user.opc_email_verified === 'boolean';
}
